interface PushEventParams {
  event?: string;
  eventTag: string;
  text?: string;
  attributes?: string;
  callback?: Function;
  attrs?: {
    [key: string]: string;
  };
}

/**
 * 推送事件到全局事件监听器
 * 
 * @param {PushEventParams} params - 事件参数对象
 * @param {string} [params.event='click'] - 事件类型，默认为'click'
 * @param {string} params.eventTag - 事件标签
 * @param {string} params.text - 事件描述文本
 * @param {any} params.attrs - 事件附加属性
 * @param {Function} [params.callback=() => {}] - 事件回调函数，默认为空函数
 * 
 * @returns {void}
 * 
 * @throws {Error} 捕获并记录任何在推送事件过程中发生的错误
 */
export function pushEvent({ event = 'click', eventTag, text, attrs, callback = () => {} }: PushEventParams): void {
  try {
    // 2025.5.15 不再使用原始 xflow 方法上报
    // (window as any)._XFLOW_.pushEvent([event, eventTag, text, attrs, callback]);
    (window as any).__SERAPH_HAS_MONITOR__.sendCustomEvent({
      name: eventTag,
      level: 'warn',
      source: text,
      value: attrs,
      error: new Error('pushEvent'), // 错误实例
    }, { defer: false });

    pushTrackEvent({
      event,
      eventTag,
      text,
      attrs,
    });
  } catch (e) {
    console.log(e);
  }
}

/**
 * 广告弹窗事件动作类型
 */
export type Action = 'open' | 'button' | 'close';

/**
 * 推送跟踪事件
 * 
 * @param {Object} params - 事件参数对象
 * @param {string} [params.event='click'] - 事件类型，默认为'click'
 * @param {string} params.eventTag - 事件标签，用于额外信息
 * @param {string} params.text - 事件文本内容
 * @param {Object} params.attrs - 事件附加属性
 * @returns {void}
 */
export function pushTrackEvent({ event = 'click', eventTag, text, attrs = {} }: PushEventParams): void {
  try {
    const { ZAHLWYY_CLICK_CONTENT } = attrs;
    // commonClick 是个能在 xflow 中追溯的字段，用于追踪用户点击事件
    const commonClickValue = `${eventTag}`;
    (window as any).__track__[event](commonClickValue, { text, ...attrs });
    (window as any).__track__[event]('', { event_name: commonClickValue, text, ...attrs });
  } catch (e) {
    console.log(e);
  }
}

/**
 * 处理并推送事件到多个跟踪系统
 * 
 * @param {Array|Object} options - 事件配置，可以是数组格式 [event, eventTag, text, attrs] 或对象格式
 * @param {string} [supplement=''] - 附加到属性内容后的补充字符串，会与下划线连接
 * 
 * 当传入数组时，会解构为 event, eventTag, text, attrs
 * 当传入对象时，会使用对象属性并构造特定格式的 attrs
 * 
 * 函数会同时调用 pushEvent 和 pushTrackEvent 进行事件推送
 */
export const xflowPushEvent = (options, supplement = '') => {
  try {
    let opt = {} as any;
    if (Array.isArray(options)) {
      const [ event, eventTag, text, attrs ] = options;
      opt = {
        event,
        eventTag,
        text,
        attrs,
      }
    } else {
      opt = {
        ...options,
      };
  
      if (supplement) {
        opt.attrs = { ZAHLWYY_CLICK_CONTENT: `${options.attributes}${supplement ? '_' + supplement : ''}` }
      }
    }
    pushEvent(opt);
  } catch (e) {
    sendMonitorEvent('xflowPushEvent_ERROR', 'error', `错误信息: ${JSON.stringify(e)}, 选项: ${JSON.stringify(options)}`);
  }
};

/**
 * 设置用户登录状态
 * 
 * 该函数接收一个用户ID字符串，解码后设置到全局_XFLOW_对象的登录状态中
 * 
 * @param userId - 需要设置的用户ID（URL编码格式）
 */
export const xflowSetLogin = (userId: string) => {
  // (window as any)._XFLOW_.setLogin(decodeURIComponent(userId));
  window.__track__?.login(decodeURIComponent(userId));
}

// monitor.ts
/**
 * 发送自定义监控事件
 *
 * @param eventTag 事件标识
 * @param level 日志级别（如：warn、error等）
 * @param source 错误来源描述
 * @param attrs 额外属性
 */
export const sendMonitorEvent = (
  eventTag: string,
  level: 'warn' | 'error' | 'info' | 'debug' | 'log',
  source: string,
  attrs: Record<string, any> = {}
) => {
  try {
    if ((window as any).__SERAPH_HAS_MONITOR__) {
      const { originError, stack, originalStack, ...rest } = attrs;
      
      // 关键修复：确保传递给监控 SDK 的错误对象包含正确的堆栈信息
      // 当使用 __webpack_public_path__ 设置 CDN 路径时，监控 SDK 可能会重新捕获堆栈
      // 因此需要确保错误对象的 stack 属性在传递前就已经设置好
      let errorForMonitor: Error;
      
      // 优先使用 originalStack（从 errorHandle 中保存的原始堆栈），其次是 stack
      const finalStack = originalStack || stack;
      // 获取堆栈数组（如果存在）
      const stackArray = (rest as any).stackArray;
      
      if (originError) {
        // 如果提供了 originError，确保其 stack 属性被正确设置
        // 使用 getter 方式，确保即使监控 SDK 重新访问 stack 属性，也能返回原始堆栈
        if (finalStack) {
          // 保存原始堆栈的引用
          const preservedStackValue = finalStack;
          // 检查 stack 属性是否已经被定义为 getter（在 errorHandle 中可能已经设置）
          const stackDescriptor = Object.getOwnPropertyDescriptor(originError, 'stack');
          
          // 如果 stack 属性还没有被定义为 getter，或者当前值不是我们想要的，则重新定义
          if (!stackDescriptor || !stackDescriptor.get || originError.stack !== finalStack) {
            Object.defineProperty(originError, 'stack', {
              get: function() {
                // 返回原始堆栈，而不是当前调用栈
                return preservedStackValue;
              },
              set: function() {
                // 阻止设置，保持原始堆栈
              },
              configurable: false,
              enumerable: true,
            });
          }
        }
        errorForMonitor = originError;
      } else if (finalStack) {
        // 如果没有 originError 但有 stack 信息，创建一个包含 stack 的错误对象
        const syntheticError = new Error(rest.message || eventTag);
        const preservedStackValue = finalStack;
        Object.defineProperty(syntheticError, 'stack', {
          get: function() {
            return preservedStackValue;
          },
          set: function() {},
          configurable: false,
          enumerable: true,
        });
        errorForMonitor = syntheticError;
      } else {
        // 如果既没有 originError 也没有 stack，创建一个默认错误
        errorForMonitor = new Error('pushEvent');
      }
      
      // 确保 value 中包含完整的堆栈信息，作为备用方案
      // 这样即使监控 SDK 重新捕获堆栈，我们也能从 value 中恢复完整的堆栈信息
      // 关键：同时传递字符串格式和数组格式的堆栈，确保监控 SDK 能够正确解析
      const { stackArray: _, ...restWithoutStackArray } = rest;
      const valueWithStack = {
        ...restWithoutStackArray,
        // 将完整的堆栈信息放在 value 中，确保不会丢失
        // 使用 originalStack 字段，这样即使监控 SDK 重新捕获堆栈，也能从 value 中恢复
        ...(finalStack ? { originalStack: finalStack } : {}),
        // 同时传递数组格式的堆栈（监控 SDK 可能使用这种格式）
        ...(stackArray ? { stackArray: stackArray } : {}),
      };
      
      // 关键修复：在调用监控 SDK 之前，确保 error 对象的 stack 属性已经被正确设置
      // 使用同步方式调用，确保堆栈信息在传递时不会被截断
      // 如果监控 SDK 在内部重新捕获堆栈，我们至少可以从 value.originalStack 中恢复
      try {
        // 在调用前再次确认 stack 属性
        if (errorForMonitor && finalStack && errorForMonitor.stack !== finalStack) {
          const preservedStackValue = finalStack;
          Object.defineProperty(errorForMonitor, 'stack', {
            get: function() {
              return preservedStackValue;
            },
            set: function() {},
            configurable: false,
            enumerable: true,
          });
        }
      } catch (e) {
        // 如果设置失败，继续使用现有的 error 对象
        console.warn('Failed to set error stack:', e);
      }
      
      (window as any).__SERAPH_HAS_MONITOR__.sendCustomEvent(
        {
          name: eventTag,
          level,
          source,
          value: valueWithStack,
          error: errorForMonitor,
        },
        { defer: false }
      );
    }
  } catch (e) {
    console.error('监控事件发送失败:', e);
  }
};
